import { useOnSpellcheck } from '@/hooks/useOnSpellcheck'
import type { YDocument } from '@/modules/yjs/hooks'
import { Textbit, useTextbit, useSelectionStats, type TBPluginDefinition, type Descendant } from '@ttab/textbit'
import type * as Y from 'yjs'
import { ContextMenu } from './ContextMenu'
import { useAcceptedSpelling } from '@/hooks/useAcceptedSpelling'
import { Toolbar } from './Toolbar'
import { ContentMenu } from './ContentMenu'
import { DropMarker } from './DropMarker'
import { UploadMarker } from './UploadMarker'
import { Gutter } from './Gutter'
import { cn } from '@ttab/elephant-ui/utils'
import { useView } from '@/hooks/useView'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getLanguageLabel } from '@/lib/getLanguageLabel'

const EditorRoot = (props: {
  ydoc: YDocument<Y.Map<unknown>>
  readOnly?: boolean
  plugins?: TBPluginDefinition[]
  content: Y.XmlText
  onChange?: (value: Descendant[]) => void
  lang?: string
  className?: string
  children?: React.ReactNode
}) => {
  const onSpellcheck = useOnSpellcheck(props.lang)

  return (
    <Textbit.Root
      value={props.content}
      awareness={props.ydoc.provider?.awareness}
      cursor={{
        dataField: 'data',
        data: props.ydoc.user as Record<string, unknown>
      }}
      readOnly={!!props.readOnly}
      onSpellcheck={onSpellcheck}
      plugins={props.plugins}
      placeholders='multiple'
      className={cn('h-screen max-h-screen flex flex-col', props.className)}
      lang={props.lang}
      onChange={props.onChange}
    >
      {props.children}
    </Textbit.Root>
  )
}


const EditorText = (props: {
  ydoc: YDocument<Y.Map<unknown>>
  autoFocus?: boolean
  className?: string
  allowStyling?: boolean
  editorType?: string
}) => {
  const { isActive } = useView()
  const ref = useRef<HTMLDivElement>(null)
  const { isAccepted, accept, remove } = useAcceptedSpelling(props.ydoc)

  // Handle focus on active state
  useEffect(() => {
    if (isActive && ref?.current?.dataset['state'] !== 'focused') {
      setTimeout(() => {
        ref?.current?.focus()
      }, 0)
    }
  }, [isActive, ref])

  if (!props.ydoc.provider || !props.ydoc.provider.isSynced) {
    return null
  }

  return (
    <Textbit.Editable
      constraints={{ allowEdgeWhitespace: false }}
      autoFocus={props.autoFocus === true && isActive}
      isSpellingAccepted={isAccepted}
      className={cn(`outline-none
          spelling-roomy
          pt-4
          pb-4
          ps-2
          dark:text-slate-100
          grow
          pr-12
          max-w-(--breakpoint-xl)`,
      props.allowStyling !== false ? 'ms-12 pe-12' : 'ps-14 pe-12',
      props.className)}
    >
      {props.allowStyling !== false
        && (
          <>
            <DropMarker />
            <Gutter>
              <ContentMenu editorType={props.editorType} />
            </Gutter>
            <Toolbar />
          </>
        )}
      <ContextMenu onAccept={accept} onRemoveAccepted={remove} />
      <UploadMarker />
    </Textbit.Editable>
  )
}

const EditorFooter = ({ lang }: { lang?: string }) => {
  const { stats } = useTextbit()
  const selection = useSelectionStats()
  const { t } = useTranslation('editor')

  // A non-collapsed selection switches the counts to that selection's scope.
  const hasSelection = selection.characters > 0

  return (
    <>
      {lang && (
        <span
          className='mr-auto rounded-full bg-muted px-2 py-0.5 text-xs tracking-wide text-muted-foreground'
          title={`${t('footer.language')} (${lang})`}
        >
          {getLanguageLabel(lang)}
        </span>
      )}
      {hasSelection && (
        <span
          className='rounded-full bg-primary/10 px-2 py-0.5 text-xs tracking-wide text-primary'
          title={t('footer.selectionHint')}
        >
          {t('footer.selection')}
        </span>
      )}
      <div className='flex gap-2'>
        <strong>{`${t('footer.words')}:`}</strong>
        <span title={hasSelection ? t('footer.selectedWords') : t('footer.numberOfWords')}>
          {hasSelection ? selection.words : `${stats.short.words} (${stats.full.words})`}
        </span>
      </div>
      <div className='flex gap-2'>
        <strong>{`${t('footer.chars')}:`}</strong>
        <span title={hasSelection ? t('footer.selectedChars') : t('footer.numberOfChars')}>
          {hasSelection ? selection.characters : `${stats.short.characters} (${stats.full.characters})`}
        </span>
      </div>
    </>
  )
}
export const BaseEditor = {
  Root: EditorRoot,
  Text: EditorText,
  Footer: EditorFooter
}
